/**
 * uuid.js — photoId generator with iOS Safari < 15.4 fallback.
 *
 * FT-342 (2026-07-08) — capture pipeline foundation.
 * See: Maritime-Agents/seaready-app/FT-340-photos/DESIGN-BRIEF.md §2.3.
 *
 * Why the fallback matters:
 *   Ferrans (SR-DECK trial user) is on iPhone. `crypto.randomUUID` shipped in
 *   Safari 15.4 (Mar 2022). Older iOS devices exist in the field. Bricking the
 *   photo pipeline on that check would be a trial-blocker. The fallback uses
 *   `crypto.getRandomValues` — available in every browser since 2011.
 */

/**
 * Generate a namespaced photo ID.
 * @returns {string} `photo_<uuid>` where uuid is RFC 4122 v4.
 */
export function generatePhotoId() {
  return `photo_${uuidv4()}`;
}

/**
 * Return a UUID v4 string. Prefers native `crypto.randomUUID`, falls back to
 * a `crypto.getRandomValues`-based implementation for Safari < 15.4.
 * Exposed for test coverage of the fallback path.
 */
export function uuidv4() {
  const cryptoObj = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  return uuidv4Fallback(cryptoObj);
}

/**
 * RFC 4122 v4 UUID via `crypto.getRandomValues`. Sets version + variant bits
 * per spec. Exported for direct testing of the fallback branch.
 */
export function uuidv4Fallback(cryptoObj) {
  const c = cryptoObj || (typeof globalThis !== 'undefined' ? globalThis.crypto : undefined);
  if (!c || typeof c.getRandomValues !== 'function') {
    throw new Error('[uuid] crypto.getRandomValues unavailable — cannot generate photo id');
  }
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  // Per RFC 4122 §4.4: version 4 in high nibble of byte 6, variant 10xx in byte 8.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [];
  for (let i = 0; i < 16; i++) hex.push(bytes[i].toString(16).padStart(2, '0'));
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
}

export default generatePhotoId;
